/**
 * DayDashboard - Unified dashboard card that consolidates all daily info
 *
 * Replaces multiple redundant cards with ONE comprehensive view:
 * - Task stats at a glance
 * - Overdue/due today tasks with checkboxes
 * - Intelligence highlights
 * - Quick actions
 *
 * Schema:
 * {
 *   type: 'day-dashboard',
 *   date: '2026-06-09',
 *   greeting: 'Good morning, Ryan',
 *   stats: { overdue: 1, due_today: 0, pending: 5, unread_intel: 2 },
 *   tasks: [...],
 *   intelligence: [...],
 *   suggestions: [...]
 * }
 */

import React, { useState, useCallback } from 'react';
import {
  Calendar,
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  Bell,
  TrendingUp,
  ChevronRight,
  Sparkles,
  Target,
  Zap,
  MoreHorizontal,
  Plus,
  Filter,
} from 'lucide-react';
import './DayDashboard.css';

// Priority colors
const PRIORITY_COLORS = {
  urgent: 'var(--color-error)',
  high: 'var(--color-warning)',
  medium: 'var(--color-primary)',
  low: 'var(--color-text-muted)',
};

// Intelligence type config
const INTEL_TYPE_CONFIG = {
  insight: { icon: Sparkles, color: 'var(--color-primary)', label: 'Insight' },
  opportunity: { icon: Target, color: 'var(--color-success)', label: 'Opportunity' },
  alert: { icon: AlertTriangle, color: 'var(--color-warning)', label: 'Alert' },
  competitive: { icon: TrendingUp, color: 'var(--color-error)', label: 'Competitive' },
  task_suggestion: { icon: Zap, color: 'var(--color-primary)', label: 'Action' },
};

/**
 * Format date for display
 */
function formatDate(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  }
  if (date.toDateString() === tomorrow.toDateString()) {
    return 'Tomorrow';
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Get greeting based on time of day
 */
function getGreeting(name) {
  const hour = new Date().getHours();
  let greeting = 'Good morning';
  if (hour >= 12 && hour < 17) greeting = 'Good afternoon';
  if (hour >= 17) greeting = 'Good evening';

  return name ? `${greeting}, ${name}` : greeting;
}

/**
 * Calculate due date display
 */
function getDueDisplay(dueDate) {
  if (!dueDate) return null;

  const due = new Date(dueDate);
  const now = new Date();
  const diffMs = due - now;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs < 0) {
    if (diffDays === 0) return { text: `${Math.abs(diffHours)}h overdue`, urgent: true };
    return { text: `${Math.abs(diffDays)}d overdue`, urgent: true };
  }
  if (diffDays === 0) return { text: diffHours <= 2 ? `${diffHours}h` : 'Today', urgent: diffHours <= 2 };
  if (diffDays === 1) return { text: 'Tomorrow', urgent: false };
  return { text: `${diffDays}d`, urgent: false };
}

/**
 * DayDashboard Component
 */
export default function DayDashboard({
  date,
  greeting,
  userName,
  stats = {},
  tasks = [],
  intelligence = [],
  suggestions = [],
  compact = false,
  onAction,
  children,
}) {
  const [activeSection, setActiveSection] = useState('tasks');
  const [completingTasks, setCompletingTasks] = useState(new Set());

  // Compute derived stats
  const overdueTasks = tasks.filter(t => {
    if (t.status === 'completed') return false;
    if (!t.due_date) return false;
    return new Date(t.due_date) < new Date();
  });

  const dueTodayTasks = tasks.filter(t => {
    if (t.status === 'completed') return false;
    if (!t.due_date) return false;
    const due = new Date(t.due_date);
    const today = new Date();
    return due.toDateString() === today.toDateString() && due >= today;
  });

  const pendingTasks = tasks.filter(t => t.status === 'pending' || t.status === 'in_progress');
  const unreadIntel = intelligence.filter(i => !i.read);

  const computedStats = {
    overdue: stats.overdue ?? overdueTasks.length,
    due_today: stats.due_today ?? dueTodayTasks.length,
    pending: stats.pending ?? pendingTasks.length,
    unread_intel: stats.unread_intel ?? unreadIntel.length,
  };

  // Handle task completion
  const handleCompleteTask = useCallback(async (taskId) => {
    setCompletingTasks(prev => new Set([...prev, taskId]));

    try {
      if (onAction) {
        await onAction({
          type: 'complete_task',
          task_id: taskId,
          task_title: tasks.find(t => t.id === taskId)?.title,
          action: 'complete_task',
        });
      }
    } catch (error) {
      console.error('Failed to complete task:', error);
    } finally {
      setCompletingTasks(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  }, [onAction, tasks]);

  // Handle stat click for filtering
  const handleStatClick = useCallback((filter) => {
    onAction?.({ type: 'filter_tasks', filter });
  }, [onAction]);

  // Handle intel click
  const handleIntelClick = useCallback((intel) => {
    onAction?.({ type: 'view_intel', intel_id: intel.id, intel });
  }, [onAction]);

  // Handle suggestion click
  const handleSuggestionClick = useCallback((suggestion) => {
    onAction?.({
      type: 'suggestion',
      query: suggestion.query,
      action: suggestion.action,
      text: suggestion.text,
    });
  }, [onAction]);

  const displayDate = formatDate(date || new Date().toISOString());
  const displayGreeting = greeting || getGreeting(userName);

  return (
    <div className={`day-dashboard ${compact ? 'day-dashboard--compact' : ''}`}>
      {/* Header */}
      <div className="day-dashboard__header">
        <div className="day-dashboard__greeting">
          <h2>{displayGreeting}</h2>
          <span className="day-dashboard__date">
            <Calendar size={14} />
            {displayDate}
          </span>
        </div>
      </div>

      {/* Stats Row */}
      <div className="day-dashboard__stats">
        <button
          className={`day-dashboard__stat ${computedStats.overdue > 0 ? 'day-dashboard__stat--alert' : ''}`}
          onClick={() => handleStatClick('overdue')}
        >
          <span className="day-dashboard__stat-value">{computedStats.overdue}</span>
          <span className="day-dashboard__stat-label">Overdue</span>
        </button>
        <button
          className={`day-dashboard__stat ${computedStats.due_today > 0 ? 'day-dashboard__stat--warning' : ''}`}
          onClick={() => handleStatClick('due_today')}
        >
          <span className="day-dashboard__stat-value">{computedStats.due_today}</span>
          <span className="day-dashboard__stat-label">Due Today</span>
        </button>
        <button
          className="day-dashboard__stat"
          onClick={() => handleStatClick('pending')}
        >
          <span className="day-dashboard__stat-value">{computedStats.pending}</span>
          <span className="day-dashboard__stat-label">Pending</span>
        </button>
        <button
          className={`day-dashboard__stat ${computedStats.unread_intel > 0 ? 'day-dashboard__stat--info' : ''}`}
          onClick={() => handleStatClick('intel')}
        >
          <span className="day-dashboard__stat-value">{computedStats.unread_intel}</span>
          <span className="day-dashboard__stat-label">Intel</span>
        </button>
      </div>

      {/* Section Tabs */}
      <div className="day-dashboard__tabs">
        <button
          className={`day-dashboard__tab ${activeSection === 'tasks' ? 'day-dashboard__tab--active' : ''}`}
          onClick={() => setActiveSection('tasks')}
        >
          <CheckCircle2 size={14} />
          Tasks
          {(computedStats.overdue > 0 || computedStats.due_today > 0) && (
            <span className="day-dashboard__tab-badge">
              {computedStats.overdue + computedStats.due_today}
            </span>
          )}
        </button>
        <button
          className={`day-dashboard__tab ${activeSection === 'intel' ? 'day-dashboard__tab--active' : ''}`}
          onClick={() => setActiveSection('intel')}
        >
          <Bell size={14} />
          Intelligence
          {computedStats.unread_intel > 0 && (
            <span className="day-dashboard__tab-badge">{computedStats.unread_intel}</span>
          )}
        </button>
      </div>

      {/* Content Area */}
      <div className="day-dashboard__content">
        {activeSection === 'tasks' && (
          <TasksSection
            overdueTasks={overdueTasks}
            dueTodayTasks={dueTodayTasks}
            completingTasks={completingTasks}
            onComplete={handleCompleteTask}
            onAction={onAction}
          />
        )}

        {activeSection === 'intel' && (
          <IntelligenceSection
            intelligence={intelligence}
            onIntelClick={handleIntelClick}
          />
        )}
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="day-dashboard__suggestions">
          {suggestions.map((suggestion, idx) => (
            <button
              key={idx}
              className="day-dashboard__suggestion"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <Sparkles size={12} />
              {suggestion.text}
            </button>
          ))}
        </div>
      )}

      {children}
    </div>
  );
}

/**
 * TasksSection - Task list within dashboard
 */
function TasksSection({ overdueTasks, dueTodayTasks, completingTasks, onComplete, onAction }) {
  const allTasks = [...overdueTasks, ...dueTodayTasks];

  if (allTasks.length === 0) {
    return (
      <div className="day-dashboard__empty">
        <CheckCircle2 size={24} />
        <span>You're all caught up!</span>
        <button
          className="day-dashboard__add-task"
          onClick={() => onAction?.({ type: 'add_task' })}
        >
          <Plus size={14} />
          Add a task
        </button>
      </div>
    );
  }

  return (
    <div className="day-dashboard__tasks">
      {/* Overdue section */}
      {overdueTasks.length > 0 && (
        <div className="day-dashboard__task-group">
          <div className="day-dashboard__task-group-header day-dashboard__task-group-header--overdue">
            <AlertTriangle size={14} />
            Overdue
            <span className="day-dashboard__task-group-count">{overdueTasks.length}</span>
          </div>
          {overdueTasks.map(task => (
            <TaskRow
              key={task.id}
              task={task}
              isCompleting={completingTasks.has(task.id)}
              onComplete={onComplete}
              onAction={onAction}
            />
          ))}
        </div>
      )}

      {/* Due today section */}
      {dueTodayTasks.length > 0 && (
        <div className="day-dashboard__task-group">
          <div className="day-dashboard__task-group-header">
            <Clock size={14} />
            Due Today
            <span className="day-dashboard__task-group-count">{dueTodayTasks.length}</span>
          </div>
          {dueTodayTasks.map(task => (
            <TaskRow
              key={task.id}
              task={task}
              isCompleting={completingTasks.has(task.id)}
              onComplete={onComplete}
              onAction={onAction}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * TaskRow - Individual task in dashboard
 */
function TaskRow({ task, isCompleting, onComplete, onAction }) {
  const [isCompleted, setIsCompleted] = useState(false);
  const priorityColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
  const dueDisplay = getDueDisplay(task.due_date);

  const handleCheckboxClick = async (e) => {
    e.stopPropagation();
    if (isCompleting || isCompleted) return;

    setIsCompleted(true);
    await onComplete(task.id);
  };

  const handleRowClick = () => {
    onAction?.({ type: 'view_task', task_id: task.id, task_title: task.title, task });
  };

  return (
    <div
      className={`day-dashboard__task ${isCompleted ? 'day-dashboard__task--completed' : ''}`}
      style={{ '--priority-color': priorityColor }}
      onClick={handleRowClick}
    >
      <button
        className={`day-dashboard__task-checkbox ${isCompleting ? 'day-dashboard__task-checkbox--loading' : ''}`}
        onClick={handleCheckboxClick}
        disabled={isCompleting || isCompleted}
      >
        {isCompleted ? (
          <CheckCircle2 size={18} className="day-dashboard__check--done" />
        ) : (
          <Circle size={18} />
        )}
      </button>

      <div className="day-dashboard__task-content">
        <span className={`day-dashboard__task-title ${isCompleted ? 'day-dashboard__task-title--done' : ''}`}>
          {task.title}
        </span>
        <div className="day-dashboard__task-meta">
          {task.account && (
            <span className="day-dashboard__task-account">{task.account}</span>
          )}
          {dueDisplay && (
            <span className={`day-dashboard__task-due ${dueDisplay.urgent ? 'day-dashboard__task-due--urgent' : ''}`}>
              {dueDisplay.text}
            </span>
          )}
        </div>
      </div>

      <ChevronRight size={16} className="day-dashboard__task-arrow" />
    </div>
  );
}

/**
 * IntelligenceSection - Intel feed within dashboard
 */
function IntelligenceSection({ intelligence, onIntelClick }) {
  if (intelligence.length === 0) {
    return (
      <div className="day-dashboard__empty">
        <Bell size={24} />
        <span>No new intelligence</span>
        <p>You're all caught up on alerts and insights.</p>
      </div>
    );
  }

  return (
    <div className="day-dashboard__intel">
      {intelligence.slice(0, 5).map((intel, idx) => {
        const config = INTEL_TYPE_CONFIG[intel.type] || INTEL_TYPE_CONFIG.insight;
        const Icon = config.icon;

        return (
          <div
            key={intel.id || idx}
            className={`day-dashboard__intel-item ${intel.read ? 'day-dashboard__intel-item--read' : ''}`}
            onClick={() => onIntelClick(intel)}
          >
            <div
              className="day-dashboard__intel-icon"
              style={{ background: `${config.color}15`, color: config.color }}
            >
              <Icon size={14} />
            </div>
            <div className="day-dashboard__intel-content">
              <span className="day-dashboard__intel-label">{config.label}</span>
              <span className="day-dashboard__intel-text">{intel.text || intel.title}</span>
            </div>
            <ChevronRight size={16} className="day-dashboard__intel-arrow" />
          </div>
        );
      })}

      {intelligence.length > 5 && (
        <button
          className="day-dashboard__intel-more"
          onClick={() => onIntelClick({ type: 'view_all' })}
        >
          View all {intelligence.length} items
          <ChevronRight size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * MiniDayDashboard - Compact version for sidebar or widget
 */
export function MiniDayDashboard({
  stats = {},
  topTask,
  topIntel,
  onAction,
}) {
  return (
    <div className="mini-day-dashboard">
      <div className="mini-day-dashboard__stats">
        {stats.overdue > 0 && (
          <span className="mini-day-dashboard__stat mini-day-dashboard__stat--alert">
            {stats.overdue} overdue
          </span>
        )}
        {stats.due_today > 0 && (
          <span className="mini-day-dashboard__stat mini-day-dashboard__stat--warning">
            {stats.due_today} due today
          </span>
        )}
        {stats.overdue === 0 && stats.due_today === 0 && (
          <span className="mini-day-dashboard__stat mini-day-dashboard__stat--success">
            All caught up
          </span>
        )}
      </div>

      {topTask && (
        <div className="mini-day-dashboard__top-item">
          <Circle size={14} />
          <span>{topTask.title}</span>
        </div>
      )}

      {topIntel && (
        <div className="mini-day-dashboard__top-item">
          <Sparkles size={14} />
          <span>{topIntel.text || topIntel.title}</span>
        </div>
      )}
    </div>
  );
}
